import { useMemo, useState } from "react";
import { RefreshCw, ExternalLink, Receipt, Download, Pin, Pencil, X as XIcon, Plus } from "lucide-react";
import { isDateInFilter } from "../layout/GlobalDateFilter";
import Button from "../ui/Button";
import Card from "../ui/Card";
import MetricCard from "../ui/MetricCard";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import Pill from "../ui/Pill";
import { formatUSD } from "../../lib/formatters";
import { formatDate } from "../../lib/dateUtils";
import { findClientByEmail } from "../../lib/emailUtils";
import { stringifyCSV, downloadCSV } from "../../lib/csv";
import PaymentForm from "../payments/PaymentForm";
import PaymentsCSVImport from "../payments/PaymentsCSVImport";

// Payments V2 — pulls Stripe Charges directly so Ahmed can compare against
// the existing GHL-driven Payments tab before detaching GHL.

function exportFilteredToCSV(rows, matchedByCharge, offers) {
  const offerName = (id) => offers.find((o) => o.id === id)?.name || "";
  const records = rows.map((c) => {
    const matched = matchedByCharge.get(c.id);
    return {
      Date: c.date || "",
      ChargeID: c.id,
      Customer: matched?.name || c.customerName || "",
      Email: c.customerEmail || "",
      Description: c.description || "",
      Status: c.status,
      Offer: offerName(c.offerId),
      Gross: c.grossAmount,
      Fee: c.fee,
      Net: c.netAmount ?? c.grossAmount - c.fee,
      Refunded: c.refundedAmount || 0,
      Currency: c.currency || "USD",
      ReceiptURL: c.receiptUrl || "",
    };
  });
  const columns = ["Date","ChargeID","Customer","Email","Description","Status","Offer","Gross","Fee","Net","Refunded","Currency","ReceiptURL"];
  const csv = stringifyCSV(records, columns);
  const stamp = new Date().toISOString().slice(0, 10);
  downloadCSV(`stripe-charges-${stamp}.csv`, csv);
}

const STATUS_PILL = {
  succeeded:          { label: "Paid",            tone: "active" },
  refunded:           { label: "Refunded",        tone: "cancelled" },
  partially_refunded: { label: "Partial refund",  tone: "paused" },
  failed:             { label: "Failed",          tone: "cancelled" },
  pending:            { label: "Pending",         tone: "pending" },
};

export default function PaymentsV2({ state, dashboard, stripe, dateFilter, onOpenClient }) {
  const { charges, loading, refreshing, lastSyncedAt, error, refresh, updateCharge } = stripe;
  const busy = loading || refreshing;
  const [statusFilter, setStatusFilter] = useState("");
  const [offerFilter, setOfferFilter] = useState(""); // "" | "__untagged__" | "__tagged__" | offerId
  const [deptFilter, setDeptFilter] = useState(""); // "" | dept name
  const [search, setSearch] = useState("");
  const [showAddPayment, setShowAddPayment] = useState(false);

  // Charges scoped to the GLOBAL date filter (year + from/to month). The
  // global filter is the only date-scoping mechanism — local "Since" picker
  // was removed. Affects both the metric cards and the table rows so a
  // "2026 view" or "Mar–Jun 2026 view" is consistent across the whole page.
  const yearCharges = useMemo(() => {
    return charges.filter((c) => isDateInFilter(c.date, dateFilter));
  }, [charges, dateFilter]);
  const clients = state?.clients || [];
  const offers = state?.offers || [];
  const offerById = useMemo(() => new Map(offers.map((o) => [o.id, o])), [offers]);

  // List of departments to populate the filter dropdown. Includes any dept
  // that has at least one offer + the configured settings list.
  const departments = useMemo(() => {
    const set = new Set(state?.settings?.departments || []);
    for (const o of offers) if (o.department) set.add(o.department);
    return [...set].sort();
  }, [state?.settings?.departments, offers]);

  // Resolve a Stripe charge → department via its tagged offer.
  const chargeDepartment = (c) => offerById.get(c.offerId)?.department || null;

  // Resolve each charge to a client. Manual override (clientIdOverride) wins
  // over the email-based auto-match. The `pinned` flag drives the visual
  // indicator so Ahmed can see at a glance which rows are manually assigned.
  const matchedByCharge = useMemo(() => {
    const byId = new Map(clients.map((c) => [c.id, c]));
    const map = new Map();
    for (const c of charges) {
      let client = null;
      let pinned = false;
      if (c.clientIdOverride) {
        client = byId.get(c.clientIdOverride) || null;
        pinned = !!client;
      }
      if (!client) {
        client = findClientByEmail(c.customerEmail, clients) || null;
      }
      if (client || pinned) map.set(c.id, { client, pinned });
    }
    return map;
  }, [charges, clients]);

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [clients]
  );
  const [editingClientFor, setEditingClientFor] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const isTaggable = (c) => c.status === "succeeded" || c.status === "partially_refunded" || c.status === "refunded";
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkTag = async (offerId) => {
    const ids = Array.from(selectedIds);
    await Promise.all(ids.map((id) => updateCharge(id, { offerId: offerId || null })));
    clearSelection();
  };

  // Charges scoped to the active department filter. When no dept is picked,
  // this is just yearCharges. When a dept is picked, we keep only charges
  // whose tagged offer lives in that dept. Drives BOTH the summary cards
  // and the table so totals reflect what's visible.
  const deptScoped = useMemo(() => {
    if (!deptFilter) return yearCharges;
    return yearCharges.filter((c) => chargeDepartment(c) === deptFilter);
  }, [yearCharges, deptFilter, offerById]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deptScoped
      .filter((c) => !statusFilter || c.status === statusFilter)
      .filter((c) => {
        if (!offerFilter) return true;
        if (offerFilter === "__untagged__") return !c.offerId;
        if (offerFilter === "__tagged__") return !!c.offerId;
        return c.offerId === offerFilter;
      })
      .filter((c) => {
        if (!q) return true;
        const matched = matchedByCharge.get(c.id);
        return (
          (c.customerName || "").toLowerCase().includes(q) ||
          (c.customerEmail || "").toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q) ||
          (c.id || "").toLowerCase().includes(q) ||
          (matched?.name || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [deptScoped, statusFilter, offerFilter, search, matchedByCharge]);

  const summary = useMemo(() => {
    let gross = 0, fees = 0, net = 0, refunded = 0, succeededCount = 0, untaggedCount = 0;
    for (const c of deptScoped) {
      if (c.status === "succeeded" || c.status === "partially_refunded") {
        gross += c.grossAmount;
        fees += c.fee;
        net += c.netAmount ?? c.grossAmount - c.fee;
        succeededCount += 1;
        if (!c.offerId) untaggedCount += 1;
      }
      refunded += c.refundedAmount || 0;
    }
    return { gross, fees, net, refunded, count: deptScoped.length, succeededCount, untaggedCount };
  }, [deptScoped]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-semibold text-lg text-brand-ink dark:text-[var(--text-primary)]">Sales</h2>
          <p className="text-xs text-brand-slate mt-0.5">Live from Stripe. Source of truth for collected revenue.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={refresh} disabled={busy} title={refreshing ? "Pulling fresh from Stripe..." : lastSyncedAt ? `Last synced ${new Date(lastSyncedAt).toLocaleString()}` : "Refresh from Stripe"}>
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
            {refreshing ? "Syncing Stripe..." : loading ? "Loading..." : "Refresh from Stripe"}
          </Button>
          <PaymentsCSVImport
            clients={state.clients}
            offers={state.offers}
            onCommit={dashboard.addPaymentsBulk}
            onCreateClient={dashboard.addClient}
            onCreateOffer={dashboard.addOffer}
          />
          <Button onClick={() => setShowAddPayment(true)}>
            <Plus size={14} /> Log sale
          </Button>
        </div>
      </div>

      <PaymentForm
        open={showAddPayment}
        onClose={() => setShowAddPayment(false)}
        onSubmit={dashboard.addPayment}
        clients={state.clients}
        offers={state.offers}
      />

      {error && (
        <div className="px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MetricCard
          label="Gross paid"
          value={formatUSD(summary.gross)}
          accent="blue"
          sublabel={`${summary.succeededCount} successful charges`}
        />
        <MetricCard
          label="Stripe fees"
          value={formatUSD(summary.fees)}
          accent="amber"
          sublabel="processor cost"
        />
        <MetricCard
          label="Net to bank"
          value={formatUSD(summary.net)}
          accent="green"
          sublabel="gross - fees"
        />
        <MetricCard
          label="Refunded"
          value={formatUSD(summary.refunded)}
          accent={summary.refunded > 0 ? "red" : "default"}
          sublabel={lastSyncedAt ? `Synced ${new Date(lastSyncedAt).toLocaleTimeString()}` : ""}
        />
        <button
          type="button"
          onClick={() => setOfferFilter(offerFilter === "__untagged__" ? "" : "__untagged__")}
          className={`text-left rounded-xl border transition-colors ${offerFilter === "__untagged__" ? "ring-2 ring-amber-400" : "hover:border-amber-300"}`}
          title={offerFilter === "__untagged__" ? "Clear untagged filter" : "Show only untagged paid charges"}
        >
          <MetricCard
            label="Untagged"
            value={summary.untaggedCount.toLocaleString()}
            accent={summary.untaggedCount > 0 ? "amber" : "default"}
            sublabel={summary.untaggedCount > 0 ? "click to filter & tag" : "all paid charges tagged"}
          />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, description..."
            className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30 min-w-[260px]"
          />
          <svg className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-slate pointer-events-none" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)]"
        >
          <option value="">All statuses</option>
          <option value="succeeded">Paid only</option>
          <option value="partially_refunded">Partial refunds</option>
          <option value="refunded">Refunded</option>
          <option value="failed">Failed</option>
        </select>
        <select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className={`px-3 py-1.5 border rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] ${
            deptFilter ? "border-brand-blue text-brand-blue-dark font-medium" : "border-gray-200"
          }`}
          title="Filter by department — updates KPI cards too"
        >
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <select
          value={offerFilter}
          onChange={(e) => setOfferFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)]"
        >
          <option value="">All offers</option>
          <option value="__untagged__">Untagged only</option>
          <option value="__tagged__">Tagged only</option>
          <option disabled>──────────</option>
          {offers
            .filter((o) => !deptFilter || o.department === deptFilter)
            .map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
        </select>
        {(statusFilter || offerFilter || deptFilter || search) && (
          <span className="text-[11px] text-brand-slate">Showing {filtered.length} of {charges.length}</span>
        )}
        <div className="ml-auto">
          <Button
            variant="secondary"
            onClick={() => exportFilteredToCSV(filtered, matchedByCharge, offers)}
            disabled={filtered.length === 0}
            title="Download filtered charges as CSV"
          >
            <Download size={13} />
            Export CSV
          </Button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-brand-blue-soft border border-brand-blue/20 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)]">
          <span className="text-xs font-medium text-brand-blue-dark dark:text-[var(--text-primary)]">
            {selectedIds.size} selected
          </span>
          <span className="text-xs text-brand-slate">Tag as offer:</span>
          <select
            value=""
            onChange={(e) => { if (e.target.value !== "__noop__") bulkTag(e.target.value); }}
            className="px-2 py-1 border border-gray-200 dark:border-[var(--border-soft)] rounded text-xs bg-white dark:bg-[var(--bg-surface)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
          >
            <option value="__noop__">Choose offer...</option>
            <option value="">— Untagged —</option>
            {offers.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <button
            onClick={clearSelection}
            className="ml-auto text-xs text-brand-slate hover:text-brand-ink underline"
          >
            Clear selection
          </button>
        </div>
      )}

      <Card>
        {filtered.length === 0 ? (
          <EmptyState icon={Receipt} title="No charges in this range" message="Adjust the Since date or wait for new payments." />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH className="w-8">
                  <input
                    type="checkbox"
                    checked={filtered.filter(isTaggable).length > 0 && filtered.filter(isTaggable).every((c) => selectedIds.has(c.id))}
                    ref={(el) => {
                      if (!el) return;
                      const taggable = filtered.filter(isTaggable);
                      const selectedOnPage = taggable.filter((c) => selectedIds.has(c.id)).length;
                      el.indeterminate = selectedOnPage > 0 && selectedOnPage < taggable.length;
                    }}
                    onChange={(e) => {
                      const taggable = filtered.filter(isTaggable).map((c) => c.id);
                      if (e.target.checked) {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          taggable.forEach((id) => next.add(id));
                          return next;
                        });
                      } else {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          taggable.forEach((id) => next.delete(id));
                          return next;
                        });
                      }
                    }}
                    className="rounded border-gray-300 text-brand-blue focus:ring-brand-blue/30"
                    title="Select all taggable charges in view"
                  />
                </TH>
                <TH>Date</TH>
                <TH>Customer</TH>
                <TH>Email</TH>
                <TH>Description</TH>
                <TH>Status</TH>
                <TH>Offer</TH>
                <TH className="text-right">Gross</TH>
                <TH className="text-right">Fee</TH>
                <TH className="text-right">Net</TH>
                <TH className="text-right">Refunded</TH>
                <TH></TH>
              </TR>
            </THead>
            <tbody>
              {filtered.map((c) => {
                const pill = STATUS_PILL[c.status] || { label: c.status, tone: "neutral" };
                const match = matchedByCharge.get(c.id);
                const matchedClient = match?.client || null;
                const isPinned = !!match?.pinned;
                const isEditing = editingClientFor === c.id;
                return (
                  <TR key={c.id}>
                    <TD className="w-8">
                      {isTaggable(c) ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          className="rounded border-gray-300 text-brand-blue focus:ring-brand-blue/30"
                        />
                      ) : null}
                    </TD>
                    <TD className="whitespace-nowrap">{formatDate(c.date)}</TD>
                    <TD className="font-medium whitespace-nowrap">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <select
                            autoFocus
                            value={c.clientIdOverride || ""}
                            onChange={(e) => {
                              updateCharge(c.id, { clientIdOverride: e.target.value || null });
                              setEditingClientFor(null);
                            }}
                            onBlur={() => setEditingClientFor(null)}
                            className="px-1.5 py-0.5 border border-gray-200 dark:border-[var(--border-soft)] rounded text-xs bg-white dark:bg-[var(--bg-surface)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30 max-w-[200px]"
                          >
                            <option value="">— Auto-match by email —</option>
                            {sortedClients.map((cl) => (
                              <option key={cl.id} value={cl.id}>{cl.name}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); setEditingClientFor(null); }}
                            className="p-0.5 text-brand-slate hover:text-brand-ink"
                            title="Cancel"
                          >
                            <XIcon size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="group flex items-center gap-1">
                          {matchedClient && onOpenClient ? (
                            <button
                              type="button"
                              onClick={() => onOpenClient(matchedClient.id)}
                              className="text-brand-blue hover:underline focus:outline-none focus:underline text-left"
                              title="Open client profile"
                            >
                              {matchedClient.name}
                            </button>
                          ) : (
                            <span className="text-brand-slate">{c.customerName || "—"}</span>
                          )}
                          {isPinned && (
                            <Pin size={11} className="text-amber-600" title="Manually assigned (overrides email match)" />
                          )}
                          <button
                            type="button"
                            onClick={() => setEditingClientFor(c.id)}
                            className="p-0.5 text-brand-slate hover:text-brand-blue opacity-0 group-hover:opacity-100 transition-opacity"
                            title={isPinned ? "Change client assignment" : "Override client assignment"}
                          >
                            <Pencil size={11} />
                          </button>
                        </div>
                      )}
                    </TD>
                    <TD className="text-brand-slate text-xs whitespace-nowrap">{c.customerEmail || "—"}</TD>
                    <TD className="text-brand-slate text-xs max-w-[220px] truncate" title={c.description}>{c.description || "—"}</TD>
                    <TD><Pill status={pill.tone}>{pill.label}</Pill></TD>
                    <TD>
                      {c.status === "failed" ? (
                        <span className="text-brand-slate text-xs">—</span>
                      ) : (
                        <select
                          value={c.offerId || ""}
                          onChange={(e) => updateCharge(c.id, { offerId: e.target.value || null })}
                          className={`px-1.5 py-0.5 border border-transparent hover:border-gray-200 dark:hover:border-[var(--border-soft)] rounded text-xs bg-transparent focus:outline-none focus:ring-1 focus:ring-brand-blue/30 max-w-[160px] truncate ${c.offerId ? "text-brand-blue dark:text-brand-blue" : "text-brand-slate italic"}`}
                          title={c.offerId ? "Change offer" : "Tag this charge to an offer"}
                        >
                          <option value="">— Untagged —</option>
                          {offers.map((o) => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                          ))}
                        </select>
                      )}
                    </TD>
                    <TD className="text-right font-medium">{formatUSD(c.grossAmount)}</TD>
                    <TD className="text-right text-xs text-amber-700">{c.fee > 0 ? formatUSD(c.fee) : "—"}</TD>
                    <TD className="text-right font-medium text-emerald-700">{c.netAmount != null ? formatUSD(c.netAmount) : "—"}</TD>
                    <TD className="text-right text-xs text-red-600">{c.refundedAmount > 0 ? formatUSD(c.refundedAmount) : "—"}</TD>
                    <TD>
                      {c.receiptUrl && (
                        <a href={c.receiptUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate inline-block" title="Open Stripe receipt">
                          <ExternalLink size={13} />
                        </a>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </tbody>
            <tfoot>
              {(() => {
                let gross = 0, fee = 0, net = 0, refunded = 0;
                for (const c of filtered) {
                  gross += Number(c.grossAmount) || 0;
                  fee += Number(c.fee) || 0;
                  net += (c.netAmount != null ? Number(c.netAmount) : (Number(c.grossAmount) || 0) - (Number(c.fee) || 0));
                  refunded += Number(c.refundedAmount) || 0;
                }
                return (
                  <tr className="border-t-2 border-gray-200 dark:border-[var(--border-soft)] bg-gray-50 dark:bg-[var(--bg-soft)] font-semibold text-xs">
                    <td colSpan={7} className="px-3 py-2 text-brand-ink dark:text-[var(--text-primary)]">
                      Subtotal of {filtered.length} {filtered.length === 1 ? "charge" : "charges"} shown
                    </td>
                    <td className="px-3 py-2 text-right">{formatUSD(gross)}</td>
                    <td className="px-3 py-2 text-right text-amber-700 dark:text-amber-300">{fee > 0 ? formatUSD(fee) : "—"}</td>
                    <td className="px-3 py-2 text-right text-emerald-700 dark:text-emerald-300">{formatUSD(net)}</td>
                    <td className="px-3 py-2 text-right text-red-600 dark:text-red-400">{refunded > 0 ? formatUSD(refunded) : "—"}</td>
                    <td />
                  </tr>
                );
              })()}
            </tfoot>
          </Table>
        )}
      </Card>
    </div>
  );
}